import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

// --- Icons (from Theme) ---
const FiFileText = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
);
const FiUser = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);
const FiUpload = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
);
const FiRefreshCw = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
);
const FiChevronLeft = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="15 18 9 12 15 6"></polyline></svg>
);
const FiChevronRight = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="9 18 15 12 9 6"></polyline></svg>
);

// --- Reusable Theme Components ---
const FormInput = ({ label, icon: Icon, note, readOnly, ...props }) => (
  <div className="mb-4">
    <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
      {label} {props.required && <span className="text-[#F46653] ml-1">*</span>}
    </label>
    <div className="relative group">
      <input
        {...props}
        readOnly={readOnly}
        className={`w-full pl-10 pr-3 py-2.5 border rounded-xl text-sm font-semibold outline-none transition-all duration-300 ${
          readOnly 
            ? "bg-[#F0EFEB]/50 border-slate-200 text-slate-500 cursor-not-allowed" 
            : "bg-white border-slate-200 text-[#0C4550] placeholder-slate-400 focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] hover:border-[#0C4550]/50 shadow-sm"
        }`}
      />
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#0C4550] transition-colors">
        {Icon && <Icon size={16} />}
      </div>
    </div>
    {note && <p className="mt-1 text-[11px] text-[#0C4550]/60 font-manrope">{note}</p>}
  </div>
);

const FormTextArea = ({ label, icon: Icon, ...props }) => (
  <div className="mb-4">
    <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
      {label} {props.required && <span className="text-[#F46653] ml-1">*</span>}
    </label>
    <div className="relative group">
      <textarea
        {...props}
        className="w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-[#0C4550] placeholder-slate-400 text-sm font-medium focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] outline-none transition-all duration-300 hover:border-[#0C4550]/50 shadow-sm resize-none"
      />
      <div className="absolute top-3 left-3 pointer-events-none text-slate-400 group-focus-within:text-[#0C4550] transition-colors">
        {Icon && <Icon size={16} />}
      </div>
    </div>
  </div>
);

// Base API Endpoints
const BASE_URL = 'https://api.eurofox.in/api/v1/uploads';
const METADATA_API_URL = `${BASE_URL}/metadata`;

const Contacts = () => {
  // --- Tab State ---
  const [activeTab, setActiveTab] = useState('STATIC');

  // --- Form & Upload State ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [contactGroupName, setContactGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [message, setMessage] = useState('');

  // --- History & Processing State ---
  const [uploadHistory, setUploadHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Fixed at 5 items per page

  const fileInputRef = useRef(null);
  const allowedExtensions = ['.csv', '.xls', '.xlsx'];

  useEffect(() => {
    fetchUploadHistory();
  }, []);

  const fetchUploadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(METADATA_API_URL);
      if (response.data.success) {
        setUploadHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching metadata:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSelectedFile(null);
    setContactGroupName('');
    setDescription('');
    setMessage('');
    setUploadStatus('idle');
    setUploadProgress(0);
    setCurrentPage(1); // Reset pagination when switching tabs
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileClick = () => fileInputRef.current.click();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setMessage(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      setUploadStatus('error');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setMessage('');
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!selectedFile || !contactGroupName.trim()) {
      setMessage('File and Group Name are required.');
      setUploadStatus('error');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('contactGroupName', contactGroupName.trim());
    if (description.trim()) formData.append('description', description.trim());

    setUploadStatus('uploading');
    setMessage(`Uploading ${activeTab.toLowerCase()} file...`);

    const uploadUrl = `${BASE_URL}/contacts/${activeTab.toLowerCase()}`;

    try {
      await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setUploadStatus('success');
      setMessage(`${activeTab === 'STATIC' ? 'Static' : 'Dynamic'} upload successful! Processing started.`);
      
      setSelectedFile(null);
      setContactGroupName('');
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      fetchUploadHistory();
      startPolling();

    } catch (error) {
      setUploadStatus('error');
      setMessage(error.response?.data?.message || 'An error occurred during upload.');
    }
  };

  const startPolling = () => {
    let count = 0;
    const interval = setInterval(() => {
      fetchUploadHistory();
      count++;
      if (count >= 20) clearInterval(interval);
    }, 3000);
  };

  // --- View Format Action (Hides Link from DOM) ---
  const handleViewFormat = () => {
    const staticLink = 'https://deetyasoft-my.sharepoint.com/:x:/p/raman/IQAKMvDUbKcVQ5Ki3TRmsi4FAdqiohc9mEbVnW8f-mZ-PQw?e=7F77rA';
    const dynamicLink = 'https://deetyasoft-my.sharepoint.com/:x:/p/raman/IQABLDM5wO6aR5HP93SCjr1_AYVGGU3ktlYx0BDrmAuKU9o?e=MtjiY4';
    
    const url = activeTab === 'STATIC' ? staticLink : dynamicLink;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // --- Filtering & Pagination Logic ---
  const filteredHistory = uploadHistory.filter((upload) => {
    const type = upload.contactType || 'STATIC';
    return type === activeTab;
  });

  // Calculate items for current page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  return (
    <div className="min-h-screen bg-[#F0EFEB] p-4 font-sans flex justify-center text-[#0C4550]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
        .font-manrope { font-family: 'Manrope', sans-serif; }
      `}</style>

      <div className="w-full max-w-6xl space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/40 backdrop-blur-md px-5 py-4 rounded-2xl border border-white/50 shadow-sm">
          <div className="pl-1">
            <h1 className="text-xl font-extrabold text-[#0C4550] font-manrope">Contact Management</h1>
            <p className="text-[#0C4550]/60 text-xs font-medium mt-0.5 font-manrope">Upload and organize your audience segments</p>
          </div>
          <button
            onClick={fetchUploadHistory}
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-transparent text-[#0C4550] font-bold rounded-lg hover:bg-[#0C4550]/10 transition-all duration-300 text-xs shadow-sm font-manrope"
          >
            <FiRefreshCw size={13} className={isLoadingHistory ? "animate-spin" : ""} /> Refresh History
          </button>
        </div>

        {/* Custom Theme Tabs */}
        <div className="flex gap-2 bg-white/60 p-1.5 rounded-xl border border-white shadow-sm w-fit">
          <button
            className={`px-6 py-2 rounded-lg text-sm font-bold font-manrope transition-all duration-300 ${
              activeTab === 'STATIC' ? 'bg-[#0C4550] text-white shadow-md' : 'text-[#0C4550] hover:bg-[#0C4550]/10'
            }`}
            onClick={() => handleTabSwitch('STATIC')}
          >
            Static Contacts
          </button>
          <button
            className={`px-6 py-2 rounded-lg text-sm font-bold font-manrope transition-all duration-300 ${
              activeTab === 'DYNAMIC' ? 'bg-[#0C4550] text-white shadow-md' : 'text-[#0C4550] hover:bg-[#0C4550]/10'
            }`}
            onClick={() => handleTabSwitch('DYNAMIC')}
          >
            Dynamic Contacts
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* --- Upload Section --- */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden h-full">
              
              {/* --- Modified Upload Header with View Format Button --- */}
              <div className="px-5 py-4 bg-[#0C4550]/5 border-b border-[#F0EFEB] flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#0C4550] flex items-center justify-center text-white shadow-lg shadow-[#0C4550]/20 shrink-0">
                    <FiUpload className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0C4550] font-manrope text-sm">Upload List</p>
                    <p className="text-[#0C4550]/60 text-[10px] font-medium font-manrope leading-tight mt-0.5">
                      {activeTab === 'STATIC'
                        ? "Standard phone numbers. Only the first column is extracted."
                        : "Header file: Col 1 'number', then '{#var1}'."}
                    </p>
                  </div>
                </div>
                
                {/* View Format Button added here */}
                <button
                  type="button"
                  onClick={handleViewFormat}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-[#D0DCF2] border border-[#0C4550]/20 text-slate-800 text-[11px] font-bold rounded-lg hover:bg-[#0C4550]/10 transition-all duration-300 shadow-sm font-manrope"
                >
                  <FiFileText size={12} />
                  View Format
                </button>
              </div>

              <div className="p-5">
                <FormInput
                  label="Contact Group Name"
                  name="contactGroupName"
                  value={contactGroupName}
                  onChange={(e) => setContactGroupName(e.target.value)}
                  placeholder={activeTab === 'STATIC' ? "e.g., Summer Promo Static" : "e.g., Personalized Offers"}
                  icon={FiUser}
                  required
                  disabled={uploadStatus === 'uploading'}
                />

                <FormTextArea
                  label="Description (Optional)"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Notes about this contact list..."
                  icon={FiFileText}
                  rows={3}
                  disabled={uploadStatus === 'uploading'}
                />

                <div className="mb-4">
                  <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
                    Target File <span className="text-[#F46653] ml-1">*</span>
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    accept=".csv, .xls, .xlsx"
                  />
                  <button
                    onClick={handleFileClick}
                    disabled={uploadStatus === 'uploading'}
                    className="w-full px-4 py-2.5 bg-[#F9FAFB] border border-slate-200 text-[#0C4550] text-sm font-semibold rounded-xl hover:border-[#0C4550]/50 transition-all duration-300 flex items-center justify-between"
                  >
                    <span className="truncate max-w-[80%]">{selectedFile ? selectedFile.name : 'Choose .csv, .xls, .xlsx'}</span>
                    <FiFileText size={16} className="text-slate-400" />
                  </button>
                </div>

                {uploadStatus === 'uploading' && (
                  <div className="mb-4">
                    <div className="flex justify-between text-[11px] font-bold text-[#0C4550] mb-1 font-manrope">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-[#F0EFEB] rounded-full h-2">
                      <div className="bg-[#0C4550] h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  </div>
                )}

                {message && (
                  <div className={`p-3 mb-4 rounded-lg text-[12px] font-bold font-manrope border ${uploadStatus === 'error' ? 'bg-[#F46653]/10 text-[#F46653] border-[#F46653]/20' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                    {message}
                  </div>
                )}

                <div className="pt-2">
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || !contactGroupName.trim() || uploadStatus === 'uploading'}
                    className="w-full px-6 py-3 bg-[#0C4550] text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#08313a] shadow-lg shadow-[#0C4550]/20 font-manrope text-sm"
                  >
                    {uploadStatus === 'uploading' ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      <>Upload {activeTab === 'STATIC' ? 'Static' : 'Dynamic'} Data</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* --- Redesigned History & Stats Section --- */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden flex flex-col h-full">
              <div className="px-6 py-4 bg-white border-b border-[#F0EFEB] flex items-center justify-between">
                <h3 className="font-extrabold text-[#0C4550] font-manrope text-base">Upload History</h3>
                <span className="px-3 py-1 bg-[#F9FAFB] text-[#0C4550]/70 rounded-lg text-[10px] font-extrabold uppercase tracking-widest font-manrope border border-[#E2E8F0]">
                  {filteredHistory.length} Total Records
                </span>
              </div>
              
              <div className="overflow-x-auto flex-1">
                {isLoadingHistory && filteredHistory.length === 0 ? (
                  <div className="p-8 text-center text-[#0C4550]/50 text-sm font-bold font-manrope animate-pulse">
                    Loading history data...
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Date</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Group</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Type</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Status</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Total</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Valid</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Invalid</th>
                        <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">Duplicates</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((upload) => (
                          <tr key={upload.fileId} className="border-b border-[#F1F5F9] bg-white hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope whitespace-nowrap">
                              {new Date(upload.uploadTimestamp).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope whitespace-nowrap">
                              {upload.contactGroupName}
                            </td>
                            <td className="px-6 py-4">
                              {/* Matching the uploaded image 'Type' badge exactly */}
                              <span className="bg-[#E1BEE7] text-[#4A148C] px-3 py-1.5 rounded-[4px] text-[11px] font-extrabold uppercase tracking-wide font-manrope">
                                {upload.contactType || 'STATIC'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {/* Matching the uploaded image 'Status' badge exactly */}
                              <span className={`px-3 py-1.5 rounded-[4px] text-[11px] font-extrabold uppercase tracking-wide font-manrope ${
                                upload.processingCompleted 
                                  ? 'bg-[#C8E6C9] text-[#2F3640]' 
                                  : 'bg-orange-100 text-orange-700 animate-pulse'
                              }`}>
                                {upload.processingCompleted ? 'PROCESSED' : 'PROCESSING'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{upload.processingCompleted ? upload.totalContacts : '-'}</td>
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{upload.processingCompleted ? upload.validContacts : '-'}</td>
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{upload.processingCompleted ? upload.invalidContacts : '-'}</td>
                            <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{upload.processingCompleted ? upload.duplicateContacts : '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="px-6 py-10 text-center text-[#475569] text-sm font-medium font-manrope bg-[#F8F9FA]">
                            No records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              
              {/* --- Pagination Controls --- */}
              {filteredHistory.length > 0 && (
                <div className="px-6 py-4 border-t border-[#F0EFEB] bg-[#F8F9FA] flex items-center justify-between rounded-b-xl">
                  <p className="text-xs font-semibold text-[#64748B] font-manrope">
                    Showing <span className="text-[#0C4550]">{indexOfFirstItem + 1}</span> to <span className="text-[#0C4550]">{Math.min(indexOfLastItem, filteredHistory.length)}</span> of <span className="text-[#0C4550]">{filteredHistory.length}</span> entries
                  </p>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={goToPrevPage}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-[#E2E8F0] text-[#475569] text-xs font-bold font-manrope transition-colors hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft size={14} /> Previous
                    </button>
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage >= totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-[#E2E8F0] text-[#475569] text-xs font-bold font-manrope transition-colors hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div> 
      </div>
    </div>
  );
};

export default Contacts;